import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Zap, Repeat, Play, Ban } from "lucide-react";
import { api, errMsg, money, fdate } from "@/lib/api";
import { useFetch } from "@/lib/hooks";
import { PageHeader, StatusBadge, Empty, Loading, SimBadge, DemoBadge, LiveIndicator } from "@/components/common";
import CheckoutDialog from "@/components/CheckoutDialog";

export default function Payments() {
  const [params] = useSearchParams();
  const pays = useFetch("/payments/mine", { poll: 5000 });
  const mandates = useFetch("/mandates", { poll: 8000 });
  const [checkout, setCheckout] = useState(null);
  const [svc, setSvc] = useState({ service: "predial", account: "", amount: "" });
  const [fineFolio, setFineFolio] = useState(params.get("multa") || "");
  const [md, setMd] = useState({ service: "cfe", account: "", max_amount: "1500", day: "5" });
  const [cancelId, setCancelId] = useState(null);
  const [tab, setTab] = useState(params.get("multa") ? "multa" : "servicio");

  useEffect(() => { if (params.get("multa")) setCheckout({ kind: "multa", ref: params.get("multa") }); }, []); // eslint-disable-line

  const createMandate = async () => {
    try {
      await api.post("/mandates", { ...md, max_amount: Number(md.max_amount), day: Number(md.day) });
      toast.success("Domiciliación activada en Cuenta Nu");
      setMd({ ...md, account: "" });
      mandates.reload();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const runMandate = async (id) => {
    try { await api.post(`/mandates/${id}/run`); toast.success("Cargo recurrente enviado; esperando webhook de Cuenta Nu"); pays.reload(); mandates.reload(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const cancelMandate = async () => {
    try { await api.delete(`/mandates/${cancelId}`); toast.success("Domiciliación cancelada"); mandates.reload(); }
    catch (e) { toast.error(errMsg(e)); } finally { setCancelId(null); }
  };

  return (
    <div>
      <PageHeader eyebrow="Pasarela de pagos" title="Pagos y domiciliación" subtitle="Liquidación instantánea, cargos recurrentes y domiciliación de CFE, agua y predial con Cuenta Nu."
        actions={<SimBadge label="CUENTA NU SIMULADO" />} />
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="servicio" data-testid="payments-tab-servicio">Pagar servicio</TabsTrigger>
          <TabsTrigger value="multa" data-testid="payments-tab-multa">Pagar multa</TabsTrigger>
          <TabsTrigger value="domiciliacion" data-testid="payments-tab-domiciliacion">Domiciliación</TabsTrigger>
          <TabsTrigger value="historial" data-testid="payments-tab-historial">Historial</TabsTrigger>
        </TabsList>

        <TabsContent value="servicio">
          <Card className="max-w-xl"><CardContent className="p-5 space-y-3">
            <div className="space-y-1.5"><Label>Servicio</Label>
              <Select value={svc.service} onValueChange={(v) => setSvc({ ...svc, service: v })}>
                <SelectTrigger data-testid="service-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="predial">Impuesto Predial</SelectItem><SelectItem value="agua">Agua potable CAPA</SelectItem><SelectItem value="cfe">Energía eléctrica CFE</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Número de cuenta</Label><Input value={svc.account} onChange={(e) => setSvc({ ...svc, account: e.target.value })} placeholder="PRE-123456" data-testid="service-account-input" /></div>
              <div className="space-y-1.5"><Label>Monto (MXN)</Label><Input type="number" min="1" value={svc.amount} onChange={(e) => setSvc({ ...svc, amount: e.target.value })} placeholder="850" data-testid="service-amount-input" /></div>
            </div>
            <Button className="w-full btn-press" disabled={!svc.account || !svc.amount} data-testid="payments-checkout-open-button"
              onClick={() => setCheckout({ kind: "servicio", service: svc.service, account: svc.account, amount: Number(svc.amount) })}>
              <Zap className="h-4 w-4 mr-1" /> Pagar con Cuenta Nu
            </Button>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="multa">
          <Card className="max-w-xl"><CardContent className="p-5 space-y-3">
            <Label>Folio de la boleta</Label>
            <div className="flex gap-2">
              <Input value={fineFolio} onChange={(e) => setFineFolio(e.target.value)} placeholder="QR-MUL-2026-000001" className="font-mono" data-testid="fine-folio-input" />
              <Button disabled={!fineFolio} onClick={() => setCheckout({ kind: "multa", ref: fineFolio.trim().toUpperCase() })} data-testid="fine-pay-button">Consultar y pagar</Button>
            </div>
            <p className="text-xs text-muted-foreground">50% de descuento por pronto pago dentro de los primeros 10 días.</p>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="domiciliacion">
          <div className="grid gap-4 lg:grid-cols-[1fr_1.4fr]">
            <Card><CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Repeat className="h-4 w-4" />Nueva domiciliación</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <Select value={md.service} onValueChange={(v) => setMd({ ...md, service: v })}>
                  <SelectTrigger data-testid="mandate-service-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="cfe">Energía eléctrica CFE</SelectItem><SelectItem value="agua">Agua potable CAPA</SelectItem><SelectItem value="predial">Impuesto Predial</SelectItem></SelectContent>
                </Select>
                <div className="space-y-1.5"><Label>Número de servicio / cuenta</Label><Input value={md.account} onChange={(e) => setMd({ ...md, account: e.target.value })} placeholder="CFE-123456789" data-testid="mandate-account-input" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5"><Label>Tope mensual</Label><Input type="number" value={md.max_amount} onChange={(e) => setMd({ ...md, max_amount: e.target.value })} data-testid="mandate-max-input" /></div>
                  <div className="space-y-1.5"><Label>Día de cargo</Label><Input type="number" min="1" max="28" value={md.day} onChange={(e) => setMd({ ...md, day: e.target.value })} data-testid="mandate-day-input" /></div>
                </div>
                <Button className="w-full" disabled={md.account.length < 4} onClick={createMandate} data-testid="mandate-create-button">Domiciliar (+50 HGT la primera vez)</Button>
              </CardContent>
            </Card>
            <Card><CardHeader className="pb-2"><CardTitle className="text-base">Mis domiciliaciones</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                {mandates.loading ? <Loading rows={2} /> : !mandates.data?.length ? <Empty title="Sin domiciliaciones" hint="Automatiza tus pagos y nunca pierdas el descuento por pago puntual." /> : mandates.data.map((m) => (
                  <div key={m.mandate_id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border p-3" data-testid={`mandate-row-${m.mandate_id}`}>
                    <div><div className="text-sm font-medium">{m.service_label}</div><div className="text-xs text-muted-foreground">Cuenta {m.account} · día {m.day} · tope {money(m.max_amount)} · {m.charges} cargo(s)</div></div>
                    <div className="flex items-center gap-2">
                      <StatusBadge status={m.status} />
                      {m.status === "activa" && <>
                        <Button size="sm" variant="outline" onClick={() => runMandate(m.mandate_id)} data-testid={`mandate-run-${m.mandate_id}`}><Play className="h-3.5 w-3.5 mr-1" />Ejecutar ciclo</Button>
                        <Button size="sm" variant="ghost" onClick={() => setCancelId(m.mandate_id)} data-testid={`mandate-cancel-${m.mandate_id}`}><Ban className="h-3.5 w-3.5" /></Button>
                      </>}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="historial">
          <Card><CardContent className="p-4">
            <div className="flex justify-end mb-2"><LiveIndicator lastSync={pays.lastSync} /></div>
            {pays.loading ? <Loading /> : !pays.data?.length ? <Empty title="Sin pagos" /> : (
              <div className="overflow-x-auto"><Table>
                <TableHeader><TableRow><TableHead>Folio</TableHead><TableHead>Concepto</TableHead><TableHead className="hidden md:table-cell">Fecha</TableHead><TableHead className="text-right">Monto</TableHead><TableHead>Estatus</TableHead></TableRow></TableHeader>
                <TableBody>{pays.data.map((p) => (
                  <TableRow key={p.payment_id} data-testid={`payment-row-${p.folio}`}>
                    <TableCell className="font-mono text-xs">{p.folio}</TableCell>
                    <TableCell className="text-sm">{p.concept} {p.is_demo && <DemoBadge />}{p.tokens_used > 0 && <span className="text-xs text-emerald-700 ml-1">(−{p.tokens_used} HGT)</span>}</TableCell>
                    <TableCell className="hidden md:table-cell text-xs">{fdate(p.created_at)}</TableCell>
                    <TableCell className="text-right font-mono">{money(p.amount)}</TableCell>
                    <TableCell><StatusBadge status={p.status} /></TableCell>
                  </TableRow>
                ))}</TableBody>
              </Table></div>
            )}
          </CardContent></Card>
        </TabsContent>
      </Tabs>

      <CheckoutDialog open={!!checkout} request={checkout} onOpenChange={(o) => !o && setCheckout(null)} onDone={() => pays.reload()} />
      <AlertDialog open={!!cancelId} onOpenChange={(o) => !o && setCancelId(null)}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader><AlertDialogTitle>¿Cancelar domiciliación?</AlertDialogTitle><AlertDialogDescription>Los próximos cargos automáticos ya no se ejecutarán.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Volver</AlertDialogCancel><AlertDialogAction onClick={cancelMandate} data-testid="mandate-cancel-confirm">Cancelar domiciliación</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
